import { useState } from 'react';
import type { FormEvent } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { OtpDialog } from '../components/OtpDialog';
import { SignInShell } from '../components/SignInShell';
import { useAuth } from '../context/useAuth';
import { ApiError, usingMockApi } from '../lib/api';
import { APP_NAME } from '../lib/brand';
import { RESET_SIMULATED } from '../lib/passwordReset';

export function Login() {
  const { user, loading, login, otpPending, verifyOtp, cancelOtp } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [lowBandwidth, setLowBandwidth] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const returnUrl = params.get('ReturnUrl') ?? '/web';
  // Set by the Forgot password page when it sends the person back here.
  const passwordChanged = params.get('reason') === 'password_changed';

  if (!loading && user) return <Navigate to={returnUrl} replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      // A correct password opens the code step; nobody is signed in until
      // onVerify below succeeds.
      await login(email, password);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  async function onVerify(code: string) {
    await verifyOtp(code);
    navigate(returnUrl, { replace: true });
  }

  function onOtpLocked() {
    cancelOtp();
    setPassword('');
    setError('Too many incorrect codes. Please sign in again.');
  }

  return (
    <SignInShell
      overlay={
        otpPending && (
          <OtpDialog
            email={email.trim()}
            onVerify={onVerify}
            onCancel={cancelOtp}
            onLocked={onOtpLocked}
          />
        )
      }
    >
      <form className="signin-card" onSubmit={onSubmit} noValidate>
        <h2 className="signin-card-title">
          <SignInIcon />
          Sign-in
        </h2>

        {error && (
          <div className="alert" role="alert">
            {error}
          </div>
        )}
        {!error && passwordChanged && (
          <div className="notice" role="status">
            Your password was changed. Please sign in.
            {RESET_SIMULATED && ' (Simulated — your existing password still applies.)'}
          </div>
        )}

        <input
          className="signin-input"
          type="email"
          name="email"
          placeholder="User Name (email address)"
          aria-label="User Name (email address)"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoFocus
        />

        <input
          className="signin-input"
          type="password"
          name="password"
          placeholder="Password"
          aria-label="Password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />

        <label className="signin-check">
          <input
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
          />
          <span>Remember my User Name on this computer</span>
        </label>

        <label className="signin-check">
          <input
            type="checkbox"
            checked={lowBandwidth}
            onChange={(e) => setLowBandwidth(e.target.checked)}
          />
          <span>Launch {APP_NAME} in low internet speed mode</span>
        </label>

        <div className="signin-actions">
          <button type="submit" className="btn-signin" disabled={submitting}>
            <SignInIcon />
            {submitting ? 'Signing in…' : 'Sign-in'}
          </button>
          <button
            type="button"
            className="btn-forgot"
            onClick={() => navigate('/forgot-password')}
          >
            Forgot Password
          </button>
        </div>
      </form>

      <p className="demo-hint">
        {usingMockApi ? (
          <>
            Demo account — <code>test@test.com</code> / <code>Test123$</code>. Superadmin —{' '}
            <code>admin@test.com</code>, same password.
          </>
        ) : (
          <>
            Sign in with an account from the portal database. The superadmin is{' '}
            <code>superadmin@groupisa.com</code>. The password is not checked until Microsoft SSO
            is connected.
          </>
        )}
      </p>
    </SignInShell>
  );
}

function SignInIcon() {
  return (
    <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
      <path
        d="M9.5 1.5h4a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1h-4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <path
        d="M1.5 8h8M6.8 4.9 9.9 8l-3.1 3.1"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
